import { scrollBarStyles } from "@/utils/theme";
import { FC, useMemo, useRef } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useDndScrolling } from "react-dnd-scrolling";
import styled, { css } from "styled-components";
import update from "immutability-helper";

import DraggableRow from "./DraggableRow";
import Text from "../Text";

const Body = styled.div`
  display: flex;
`;

const Columns = styled.div`
  padding: 24px;
  ${scrollBarStyles};
  max-height: 550px;
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  &:first-of-type {
    border-right: 1px solid ${({ theme }) => theme.colors.lightGray};
  }
`;

const Checkmark = styled.span<{ $checked: boolean }>`
  width: 16px;
  height: 16px;
  border: 2px solid
    ${({ theme, $checked }) =>
      $checked ? theme.colors.purple : theme.colors.gray};
  background: ${({ theme, $checked }) =>
    $checked ? theme.colors.purple : theme.colors.white};
  border-radius: 1px;
  position: relative;
  transition: 0.2s ease-out all;
  &::after {
    content: "";
    position: absolute;
    left: 4px;
    top: 1px;
    width: 5px;
    height: 9px;
    border: solid ${({ theme }) => theme.colors.white};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    display: ${({ $checked }) => ($checked ? "block" : "none")};
  }
`;

interface RowProps {
  isDragging?: boolean;
  isOver?: boolean;
  hovered?: boolean;
}

export const Row = styled.div<RowProps>`
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 12px 13px 16px;
  cursor: pointer;
  transition: 0.2s ease-out border-color;

  ${({ theme, isDragging }) =>
    isDragging &&
    css`
      pointer-events: none;
      border-color: ${theme.colors.purple};
    `}
  ${({ theme, isOver }) =>
    isOver &&
    css`
      border-style: dashed;
      border-color: ${theme.colors.purple};
    `}
`;

const DndColumns = ({ children }: { children: React.ReactNode }) => {
  const ref = useRef<HTMLDivElement>(null);
  useDndScrolling(ref, { strengthMultiplier: 100 });

  return <Columns ref={ref}>{children}</Columns>;
};

interface Props {
  columns: string[];
  exportables: string[];
  onColumnsReorder: (reorderedColumns: string[]) => void;
  toggleColumn: (column: string) => void;
}

const ColumnsSelection: FC<Props> = ({
  columns,
  exportables,
  onColumnsReorder,
  toggleColumn,
}) => {
  const selectableColumns = useMemo(() => {
    if (!columns) return [];

    return columns.filter((col) => !exportables.includes(col));
  }, [columns, exportables]);

  const reorderRows = (dragIndex: number, hoverIndex: number) => {
    const cols = update(exportables, {
      $splice: [
        [dragIndex, 1],
        [hoverIndex, 0, exportables[dragIndex]],
      ],
    });

    onColumnsReorder(cols);
  };

  return (
    <Body>
      <Columns>
        {selectableColumns.map((c) => (
          <Row key={c} onClick={() => toggleColumn(c)}>
            <Text $type="smallBold">{c}</Text>
            <Checkmark $checked={false} />
          </Row>
        ))}
      </Columns>
      <DndProvider backend={HTML5Backend}>
        <DndColumns>
          {exportables.map((c, index) => (
            <DraggableRow
              key={c}
              onClick={() => toggleColumn(c)}
              index={index}
              columnName={c}
              reorderRows={reorderRows}
            >
              <Text $type="smallBold">{c}</Text>
              <Checkmark $checked={true} />
            </DraggableRow>
          ))}
        </DndColumns>
      </DndProvider>
    </Body>
  );
};

export default ColumnsSelection;
